import React, { useEffect, useRef, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import SubmissionCardModalDeclineReasons from '@components/Submission/Card/Modal/Decline/Reasons/Reasons'
import { DECINE_REASON_CHARACTER_LIMIT } from '@constants'
import { ICampaignContextProps } from '@context/Campaign'
import { ISelectedSubmissionsContextProps } from '@context/SelectedSubmissions'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import { EventTrackingNames } from '@enums'
import { withCampaign, withSelectedSubmissions, withSubmissionListFilter } from '@hocs'
import { usePrevious } from '@hooks/usePrevious'
import { IApplicationState } from '@store'
import { ICategorisedDeclineReasons, selectDeclineReasons } from '@store/rootResource'
import { selectIsDeclining } from '@store/submissions'
import { bulkDecline } from '@store/submissions/actions/bulkDecline'
import {
  Button,
  CloseIcon,
  Form,
  IWithFormValidationProps,
  Modal,
  ModalTitle,
  TextArea,
  Tip,
} from '@tribegroup'
import { insertStringBetweenText, isEmpty, toEventsTracking } from '@utils'

export interface IInboxTitleBarSelectionModeBulkDeclineProps {
  submissionIds: ReadonlyArray<number>
  campaignId: number
  isOpen?: boolean
  onCancel: VoidFunction
}

interface IProps
  extends IInboxTitleBarSelectionModeBulkDeclineProps,
    ICampaignContextProps,
    IWithFormValidationProps,
    ISelectedSubmissionsContextProps,
    ISubmissionListFilterContextProps {}

export const InboxTitleBarSelectionModeBulkDecline: React.FC<IProps> = ({
  submissionIds,
  isOpen = false,
  onCancel,
  campaign,
  emptySelection,
  filter,
  sortBy,
  validate,
  setForm,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const isDeclining = useSelector((state: IApplicationState) => selectIsDeclining(state))
  const reasons = useSelector((state: IApplicationState) =>
    selectDeclineReasons(state, campaign.supported_submission_types[0]),
  ) as ReadonlyArray<ICategorisedDeclineReasons>

  const [declineReason, setDeclineReason] = useState('')
  const [tip, setTip] = useState('')

  const prevIsDeclining = usePrevious(isDeclining)
  const textArea = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (prevIsDeclining && !isDeclining) {
      onCancel()
      emptySelection()
    }
  }, [isDeclining])

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const declineReasonTrimmed = declineReason
      .trim()
      .split(
        intl.formatMessage({
          id: 'submission.decline.form.firstname.placeholder',
        }),
      )
      .join('{firstName}')

    const analytics = toEventsTracking(EventTrackingNames.BulkDeclineSubmissions, {
      brief_id: campaign.id,
      role: campaign.role,
      count: submissionIds.length,
      current_filter: filter,
      message: declineReasonTrimmed,
      brief_state: campaign.status,
    })

    if (
      declineReasonTrimmed.length > 0 &&
      declineReasonTrimmed.length <= DECINE_REASON_CHARACTER_LIMIT
    ) {
      dispatch(
        bulkDecline(
          campaign.id,
          {
            decline_reason: declineReasonTrimmed,
            submission_ids: submissionIds,
          },
          {
            filter,
            sortBy,
            refreshSubmissions: true,
            event: analytics,
          },
        ),
      )
    }
  }

  const onCancelClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    setDeclineReason('')
    setTip('')
    onCancel()
  }

  const onReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDeclineReason(event.currentTarget.value)
  }

  const onReasonClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    const ta = textArea.current as HTMLTextAreaElement
    const reasonSelected = event.currentTarget as HTMLElement
    const newDeclineReason = insertStringBetweenText(
      ta.value,
      reasonSelected.getAttribute('data-text') || '',
      ta.selectionStart,
    )
    setDeclineReason(newDeclineReason)
    setTip(reasonSelected.getAttribute('data-show-tip') || '')
  }

  return (
    <Modal reactModalProps={{ isOpen, onRequestClose: onCancel }} width={50} complex>
      <ModalTitle center>
        <FormattedMessage
          id="submission.decline.form.bulk.title"
          values={{ value: submissionIds.length }}
        />
      </ModalTitle>
      <CloseIcon onClick={onCancelClick} />
      <Form onSubmit={onSubmit} onBeforeSubmit={validate} setForm={setForm} method="POST">
        <TextArea
          height={8}
          placeholder={intl.formatMessage({
            id: 'submission.decline.form.reason.placeholder',
          })}
          innerRef={textArea}
          value={declineReason}
          onChange={onReasonChange}
          characterLimit={DECINE_REASON_CHARACTER_LIMIT}
        />
        <SubmissionCardModalDeclineReasons reasons={reasons} onReasonClick={onReasonClick} />
        <Flex justifyBetween>
          <div>{tip && <Tip text={tip} />}</div>
          <Flex justifyEnd>
            <Button
              scheme="primary"
              outlined
              onClick={onCancelClick}
              fontSize={0.875}
              height={3}
              width={10.125}
            >
              <FormattedMessage id="core.text.cancel" />
            </Button>
            <Button
              scheme="primary"
              fontSize={0.875}
              height={3}
              width={10.125}
              leftOuterSpacing={1}
              disabled={isDeclining || isEmpty(declineReason.trim())}
              loading={isDeclining}
            >
              <FormattedMessage id="core.text.submit" />
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}

export default withSelectedSubmissions(
  withSubmissionListFilter(withCampaign(InboxTitleBarSelectionModeBulkDecline)),
)
